import React from 'react'
import { Pulse } from 'better-react-spinkit'
import { Form, Field } from 'react-final-form'
import { HeadingPrimary } from 'Styles/Typography'
import { compose, withState, lifecycle, withHandlers, branch, renderComponent } from 'recompose'
import { get, update, getTimestamp } from 'Services/Firebase'
import { ClaimWrapper, FormPageWrapper } from './Styles'
import {
  Button,
  FieldLabel,
  FieldError,
  FinalTextArea,
  FinalButtonGroup,
  FormFieldWrapper,
  FormActionsWrapper,
  SweetAlert,
  SigPad
} from 'Components'

const booleanOptions = [
  {
    value: 'Yes',
    label: 'Yes'
  },
  {
    value: 'No',
    label: 'No'
  },
  {
    value: 'N/A',
    label: 'N/A'
  }
]

const validate = hasSig => values => {
  const errors = {}
  if (!values.insuranceClaimsRepRating) {
    errors.insuranceClaimsRepRating = 'Required'
  }

  if (!values.plumberRating) {
    errors.plumberRating = 'Required'
  }

  if (!values.roofAccess) {
    errors.roofAccess = 'Required'
  }

  if (!values.excessSettled) {
    errors.excessSettled = 'Required'
  }

  if (!values.leakDetectionCarriedOut) {
    errors.leakDetectionCarriedOut = 'Required'
  }

  if (!values.plumberDamage) {
    errors.plumberDamage = 'Required'
  }

  if (!hasSig) {
    errors.sig = 'Required'
  }

  return errors
}

const sigCanvas = React.createRef()

const ClaimContainer = ({ status: { hasSig, error, completed }, submit, claimDetails, updateStatus }) => (
  <FormPageWrapper>
    <ClaimWrapper>
      <HeadingPrimary>Service Questionnaire</HeadingPrimary>
      <Form
        validate={validate(hasSig)}
        initialValues={claimDetails}
        onSubmit={submit}
      >
        {
          ({ handleSubmit, submitting, values, submitFailed }) => (
            <form onSubmit={handleSubmit}>
              <FormFieldWrapper>
                <FieldLabel>Rate the performance of the <strong>insurance claims representative</strong>.</FieldLabel>
                <Field
                  name='insuranceClaimsRepRating'
                  component={FinalButtonGroup}
                  options={[
                    {
                      value: 'Good',
                      label: 'Good'
                    },
                    {
                      value: 'Fair',
                      label: 'Fair'
                    },
                    {
                      value: 'Poor',
                      label: 'Poor'
                    }
                  ]}
                  disabled={!!claimDetails}
                />
              </FormFieldWrapper>
              <FormFieldWrapper>
                <FieldLabel>Rate the performance of the <strong>plumber</strong> who carried out the remedial work.</FieldLabel>
                <Field
                  name='plumberRating'
                  component={FinalButtonGroup}
                  options={[
                    {
                      value: 'Good',
                      label: 'Good'
                    },
                    {
                      value: 'Fair',
                      label: 'Fair'
                    },
                    {
                      value: 'Neutral',
                      label: 'Neutral'
                    },
                    {
                      value: 'Poor',
                      label: 'Poor'
                    }
                  ]}
                  disabled={!!claimDetails}
                />
              </FormFieldWrapper>
              {
                values && values.plumberRating === 'Poor' && <FormFieldWrapper>
                  <FieldLabel>Why was the plumber service poor?</FieldLabel>
                  <Field
                    name='plumberServiceComment'
                    component={FinalTextArea}
                    disabled={!!claimDetails}
                  />
                </FormFieldWrapper>
              }
              <FormFieldWrapper>
                <FieldLabel>Did the plumber gain access through the roof and not the ceiling?</FieldLabel>
                <Field
                  name='roofAccess'
                  component={FinalButtonGroup}
                  options={booleanOptions}
                  disabled={!!claimDetails}
                />
              </FormFieldWrapper>
              <FormFieldWrapper>
                <FieldLabel>Did you settle the first amount payable(Excess) with the plumber?</FieldLabel>
                <Field
                  name='excessSettled'
                  component={FinalButtonGroup}
                  options={booleanOptions}
                  disabled={!!claimDetails}
                />
              </FormFieldWrapper>
              <FormFieldWrapper>
                <FieldLabel>In the instance of a concealed pipe leak was leak detection carried out?</FieldLabel>
                <Field
                  name='leakDetectionCarriedOut'
                  component={FinalButtonGroup}
                  options={booleanOptions}
                  disabled={!!claimDetails}
                />
              </FormFieldWrapper>
              <FormFieldWrapper>
                <FieldLabel>Did the plumber accidentally damage your property?</FieldLabel>
                <Field
                  name='plumberDamage'
                  component={FinalButtonGroup}
                  options={[
                    {
                      value: 'Yes',
                      label: 'Yes'
                    },
                    {
                      value: 'No',
                      label: 'No'
                    }
                  ]}
                  disabled={!!claimDetails}
                />
              </FormFieldWrapper>
              {
                values && values.plumberDamage === 'Yes' && <FormFieldWrapper>
                  <FieldLabel>Please explain the damage caused by the plumber</FieldLabel>
                  <Field
                    name='plumberDamageComment'
                    component={FinalTextArea}
                    disabled={!!claimDetails}
                  />
                </FormFieldWrapper>
              }
              <FormFieldWrapper>
                <FieldLabel>Further comments and arrangements</FieldLabel>
                <Field
                  name='furtherComments'
                  component={FinalTextArea}
                  disabled={!!claimDetails}
                />
              </FormFieldWrapper>
              {
                !claimDetails && (
                  <FormFieldWrapper>
                    <FieldLabel>Signature</FieldLabel>
                    <SigPad
                      allowClear
                      ref={sigCanvas}
                      onEnd={() => updateStatus({ hasSig: true })}
                      onCleared={() => updateStatus({ hasSig: false })}
                    />
                    {submitFailed && !hasSig && <FieldError>Required</FieldError>}
                  </FormFieldWrapper>
                )
              }
              <FormActionsWrapper>
                <Button
                  fullWidth
                  type='submit'
                  processing={submitting}
                  disabled={!!claimDetails}
                >
                  Submit survey
                </Button>
              </FormActionsWrapper>
            </form>
          )
        }
      </Form>
    </ClaimWrapper>
    <SweetAlert
      isOpen={completed}
      success={!error}
      danger={error}
      title={error ? 'Error' : 'Success'}
      text={error || 'Your survey was completed.'}
      onConfirm={() => updateStatus({ completed: false })}
    />
  </FormPageWrapper>
)

const initialStatus = {
  loading: false,
  error: null,
  completed: false,
  hasSig: false
}

export default compose(
  withState('status', 'setStatus', initialStatus),
  withState('claimDetails', 'setClaimDetails', null),
  withHandlers({
    updateStatus: ({ status, setStatus }) => update => setStatus({ ...status, ...update })
  }),
  lifecycle({
    async componentDidMount () {
      const { match: { params: { claimId } }, history } = this.props
      this.props.updateStatus({ loading: true })
      const { value } = await get(`claims/${claimId}`);
      if (value && !value.inactivePolicy) {
        history.push(`/claim/${claimId}`)
      } else if (value && value.signedAt) {
        this.props.setClaimDetails(value)
      }
      this.props.updateStatus({ loading: false })
    }
  }),
  withHandlers({
    submit: ({ updateStatus, match: { params: { claimId } = {} } = {} }) => async values => {
      try {
        const base64Signature = sigCanvas.current.toDataURL()
        const signedFrom = await fetch('https://json.geoiplookup.io/').then(res => res.json())
        await update(`claims/${claimId}`, {
          ...values,
          base64Signature,
          signedAt: getTimestamp(),
          signedFrom
        });
        updateStatus({ completed: true, error: null })
      } catch (error) {
        updateStatus({ completed: true, error: error.message })
      }
    }
  }),
  branch(({ status: { loading } }) => loading, renderComponent(() => <Pulse color='rgb(7, 71, 166)' />))
)(ClaimContainer)



// WEBPACK FOOTER //
// ./src/Containers/ClaimContainer.jsx